import type { ReactNode } from 'react'
import { AuthPanel } from './auth-panel'
import { Button, Checkbox, InputField } from '@/shared/ui'
import { designTokens } from '@/shared/lib/design-tokens'
import { buildRouteWithReturnTo, cn } from '@/shared/lib'
import { routes } from '@/shared/config'

export type AuthPanelEmailVariant = 'desktop' | 'mobile'

export type AuthPanelEmailProps = {
  registrationHref?: string
  variant?: AuthPanelEmailVariant
  title?: string
  emailLabel?: string
  emailPlaceholder?: string
  emailValue?: string
  onEmailChange?: (value: string) => void
  emailHelperText?: string
  agreementLabel?: ReactNode
  showAgreementCheckbox?: boolean
  agreementChecked?: boolean
  onAgreementChange?: (checked: boolean) => void
  legalLinksReturnTo?: string
  submitLabel?: string
  onSubmit?: () => void
  submitDisabled?: boolean
  minHeight?: string | null
  className?: string
}

const agreementPrefix = 'Принимаю'
const agreementTermsLabel = 'пользовательское соглашение'
const agreementMiddle = 'и соглашаюсь с'
const agreementPolicyLabel = 'правилами использования и обработки персональных данных'

export function AuthPanelEmail({
  variant = 'desktop',
  title = 'Вход',
  registrationHref = 'https://academy.productsense.io/register?next=/academy',
  emailLabel = 'Email',
  emailPlaceholder = 'email@domain.com',
  emailValue,
  onEmailChange,
  emailHelperText,
  agreementLabel,
  showAgreementCheckbox = true,
  agreementChecked,
  onAgreementChange,
  legalLinksReturnTo,
  submitLabel = 'Продолжить',
  onSubmit,
  submitDisabled = false,
  minHeight,
  className,
}: AuthPanelEmailProps) {
  const isMobile = variant === 'mobile'
  const footerGap = isMobile ? designTokens.spacing.none : designTokens.spacing.popupModalGap

  const checkboxId = 'auth-agreement-checkbox'
  const agreementTextId = 'auth-agreement-text'
  const userAgreementHref = buildRouteWithReturnTo(routes.userAgreement, legalLinksReturnTo)
  const privacyPolicyHref = buildRouteWithReturnTo(routes.privacyPolicy, legalLinksReturnTo)

  const checkbox = (
    <Checkbox
      id={checkboxId}
      checked={agreementChecked}
      onChange={(event) => onAgreementChange?.(event.target.checked)}
      aria-describedby={agreementTextId}
    />
  )

  const linkStyle = {
    color: designTokens.colors.text.primary,
    textDecoration: 'underline',
    font: 'inherit',
    display: 'inline',
    cursor: 'pointer',
    textAlign: 'left',
    verticalAlign: 'baseline',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    hyphens: 'auto',
    boxSizing: 'border-box',
    lineHeight: 'inherit',
  } as const

  const agreementText = (
    <span
      id={agreementTextId}
      style={{
        textAlign: 'left',
        display: 'block',
        width: '100%',
        wordBreak: 'normal',
        overflowWrap: 'anywhere',
      }}
    >
      {agreementPrefix}{' '}
      <a href={userAgreementHref} target="_blank" rel="noreferrer" style={linkStyle}>
        {agreementTermsLabel}
      </a>{' '}
      {agreementMiddle}{' '}
      <a href={privacyPolicyHref} target="_blank" rel="noreferrer" style={linkStyle}>
        {agreementPolicyLabel}
      </a>
    </span>
  )

  const agreementTextStyle = {
    color: designTokens.colors.text.primary,
    fontFamily: designTokens.typography.fontFamilies.primary,
    fontSize: designTokens.typography.body.xsNormal.size,
    lineHeight: designTokens.typography.body.xsNormal.lineHeight,
    fontWeight: designTokens.typography.body.xsNormal.weight,
    textAlign: 'left' as const,
    display: 'block',
    width: '100%',
    textAlignLast: 'left' as const,
    wordBreak: 'normal' as const,
    overflowWrap: 'anywhere' as const,
    alignSelf: 'flex-start' as const,
  }

  return (
    <AuthPanel
      variant={isMobile ? 'mobile' : 'desktop'}
      title={title}
      minHeight={minHeight ?? (isMobile ? null : undefined)}
      className={cn(className)}
      onSubmit={onSubmit}
      footer={
        <div
          className="flex w-full flex-col"
          style={{ gap: footerGap, marginTop: isMobile ? '30px' : undefined }}
        >
          <Button
            type="submit"
            variant={isMobile ? 'primaryMobile' : 'primary'}
            disabled={submitDisabled}
            className="w-full"
          >
            {submitLabel}
          </Button>
          {/* Регистрации в этом приложении больше нет: воркфлоу, заводивший
              учётку, подменён заглушкой и всегда отвечает отказом. Новых людей
              заводит Академия, туда и ведём — иначе человек будет пытаться
              зарегистрироваться там, где это невозможно. */}
          <p
            style={{
              fontFamily: designTokens.typography.fontFamilies.primary,
              fontSize: designTokens.typography.body.base.size,
              lineHeight: 'normal',
              fontWeight: designTokens.typography.body.base.weight,
              color: designTokens.colors.text.secondary,
              margin: 0,
              textAlign: 'center',
            }}
            data-testid="auth-registration-hint"
          >
            Нет аккаунта?{' '}
            <a
              href={registrationHref}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: designTokens.colors.text.primary }}
              className="transition-colors duration-150 hover:opacity-80"
              data-testid="auth-registration-link"
            >
              Зарегистрируйтесь в Академии
            </a>
          </p>
          {!isMobile ? (
            showAgreementCheckbox ? (
              <div
                className="flex w-full items-start"
                style={{
                  gap: designTokens.spacing.xs,
                  textAlign: 'left',
                  justifyContent: 'flex-start',
                  alignContent: 'flex-start',
                }}
              >
                {checkbox}
                <span style={agreementTextStyle}>{agreementLabel ?? agreementText}</span>
              </div>
            ) : null
          ) : null}
        </div>
      }
    >
      <div
        className="flex w-full flex-col"
        style={{ gap: isMobile ? designTokens.spacing.xxl : designTokens.spacing.none }}
      >
        <InputField
          label={emailLabel}
          placeholder={emailPlaceholder}
          name="email"
          type="email"
          value={emailValue}
          onChange={(event) => onEmailChange?.(event.target.value)}
          helperText={emailHelperText}
        />
        {isMobile && showAgreementCheckbox ? (
          <div
            className="flex w-full items-start"
            style={{
              gap: designTokens.spacing.xs,
              textAlign: 'left',
              justifyContent: 'flex-start',
              alignContent: 'flex-start',
            }}
          >
            {checkbox}
            <span style={agreementTextStyle}>{agreementLabel ?? agreementText}</span>
          </div>
        ) : null}
      </div>
    </AuthPanel>
  )
}
